import { sameFlagSnapshot } from '../flow/presentation/canonicalSurveyFlags';
import type { CXPublicSurveyResponse } from './cxPublicSurveyService';

export interface CXRuntimeVideo {
  id: string;
  status: 'ready' | 'loading' | 'unavailable';
  playbackUrl?: string;
}
export interface CXRuntimeReviewRouting {
  enabled: boolean;
  destinations: Array<{ id: string; provider: string; url: string; label?: string }>;
  fallback?: string;
}
export interface CXRuntimeContext {
  reviewRouting?: CXRuntimeReviewRouting;
  flagSnapshot?: unknown;
  organizationId: string;
  serviceMedia: Array<{ type: 'image' | 'video' | 'voice'; url?: string; thumbnailUrl?: string; label?: string; notes?: string }>;
  technicianNotes: string;
  contactPhone: string;
  capabilities: {
    hasServiceMedia: boolean;
    hasTechnicianNotes: boolean;
    canContact: boolean;
    tippingConfigured: boolean;
    legacyFinixEnabled?: boolean;
    tipEligibility: 'disabled' | 'server_check_required';
  };
  videoAssets: Record<string, CXRuntimeVideo>;
  refresh: 'reload_invite';
}

type RecordValue = Record<string, unknown>;
const record = (value: unknown): RecordValue => value && typeof value === 'object' && !Array.isArray(value) ? value as RecordValue : {};

/** Derive render data without changing the pinned response or canonical document. */
export function bindPublicRuntimeContext(response: CXPublicSurveyResponse): CXPublicSurveyResponse {
  const runtime = response.runtimeContext;
  if (!runtime) return response; // Older API/legacy contracts remain compatible.
  const survey = structuredClone(response.customerSurvey ?? {});
  const paths = Array.isArray(survey.paths) ? survey.paths : [];
  for (const pathValue of paths) {
    const path = record(pathValue);
    path.routeContext = { ...record(path.routeContext), has_service_proof: runtime.serviceMedia.length > 0, has_technician_notes: Boolean(runtime.technicianNotes), tipping_enabled: !response.testMode && runtime.capabilities.tippingConfigured };
    for (const pageValue of Array.isArray(path.pages) ? path.pages : []) {
      const page = record(pageValue);
      if (response.testMode || response.tipsDisabled || !runtime.capabilities.tippingConfigured || runtime.capabilities.tipEligibility !== 'server_check_required') {
        page.components = (Array.isArray(page.components) ? page.components : []).filter(value => !['tip_section', 'finix_tip_section'].includes(String(record(value).type)));
      }
      for (const value of Array.isArray(page.components) ? page.components : []) {
        const component = record(value);
        const config = record(component.config);
        component.config = config;
        if (component.type === 'proof_media') {
          config.sourceMode = 'runtime';
          const configured = config.videoAsset;
          const assetId = typeof configured === 'string' ? configured : record(configured).id;
          if (Object.prototype.hasOwnProperty.call(config, 'videoAsset')) {
            // A configured welcome video replaces appointment proof in the shared
            // media viewer. Resolve only the tenant-scoped runtime asset; never a
            // stored playback URL or unrelated appointment media.
            const asset = typeof assetId === 'string' && assetId.trim()
              ? runtimeVideoAsset(runtime, assetId)
              : { status: 'unavailable' as const };
            config.videoAsset = asset;
            config.items = asset.status === 'ready' && asset.playbackUrl ? [{
              type: 'video',
              url: asset.playbackUrl,
              thumbnailUrl: config.posterUrl,
              label: typeof config.title === 'string' && config.title.trim() ? config.title : component.content,
              notes: config.caption ?? component.supportingText,
            }] : [];
          } else {
            config.items = structuredClone(runtime.serviceMedia);
          }
        }
        if (component.type === 'technician_notes') component.supportingText = runtime.technicianNotes;
        if (component.type === 'video') {
          const configured = config.videoAsset ?? config.asset;
          const id = typeof configured === 'string' ? configured : record(configured).id;
          // Managed assets never fall back to a stored URL, even if the map omits them.
          config.videoAsset = typeof id === 'string' ? runtimeVideoAsset(runtime, id) : { status: 'unavailable' };
          delete config.asset;
        }
      }
    }
  }
  return { ...response, customerSurvey: survey, sourceEvent: { ...response.sourceEvent, payload: { ...response.sourceEvent?.payload, raw: { ...record(response.sourceEvent?.payload?.raw), technicianNotes: runtime.technicianNotes, notes: runtime.technicianNotes, organizationPhone: runtime.contactPhone } } } };
}

/** Also usable by canonical component adapters without mutating their snapshot. */
export function runtimeVideoAsset(runtime: CXRuntimeContext, id: string): CXRuntimeVideo {
  const asset = runtime.videoAssets[id];
  return asset?.status === 'ready' && asset.playbackUrl
    ? { id, status: 'ready', playbackUrl: asset.playbackUrl }
    : { id, status: asset?.status === 'loading' ? 'loading' : 'unavailable' };
}

export function refreshedRuntimeContext(current: CXPublicSurveyResponse, fresh: CXPublicSurveyResponse): CXRuntimeContext {
  if (!current.runtimeContext || !fresh.runtimeContext || current.runtimeContext.organizationId !== fresh.runtimeContext.organizationId || !sameFlagSnapshot(current.runtimeContext.flagSnapshot, fresh.runtimeContext.flagSnapshot) || current.triggerGroupId !== fresh.triggerGroupId || current.moduleRevisionId !== fresh.moduleRevisionId || current.executionStepId !== fresh.executionStepId || current.appointmentCode !== fresh.appointmentCode || Boolean(current.testMode) !== Boolean(fresh.testMode)) {
    throw new Error('The survey identity changed. Reopen your invitation to continue.');
  }
  return fresh.runtimeContext;
}
